/**
 * Address country data for the checkout traveler/contact forms.
 *
 * Shares its data with the nationality list (see nationalities.ts): the same ISO
 * 3166-1 alpha-2 set, German display names via `Intl.DisplayNames`, and DE/AT/CH
 * pinned to the top for the German market. The only difference is casing —
 * address country values are LOWERCASE ('de'/'at'/'ch'/...), because that is what
 * the DaVinci booking payload (Client.Address.Country / Participant.Address.Country)
 * and the form validation already expect.
 */

import type { DropdownOption } from '@/components/ui/BaseDropdown.vue';
import { NATIONALITY_CODES, PINNED_NATIONALITY_CODES, getNationalityName } from './nationalities';

// German market, fixed locale.
const collator = new Intl.Collator('de');

/** Lowercase address country codes pinned to the top, in this exact order. */
const PINNED_COUNTRY_CODES = PINNED_NATIONALITY_CODES.map((code) => code.toLowerCase());

/** Selectable address country codes (lowercase), same set as the nationalities. */
export const COUNTRY_CODES: readonly string[] = NATIONALITY_CODES.map((code) => code.toLowerCase());

/** Set of valid lowercase codes for O(1) validation lookups. */
export const COUNTRY_CODE_SET = new Set<string>(COUNTRY_CODES);

/**
 * Resolve the German country name for an address country code
 * (e.g. 'de' → 'Deutschland'). Casing is handled by getNationalityName.
 */
export function getCountryName(code: string): string {
  return getNationalityName(code);
}

/**
 * Dropdown options for the address country select: DE/AT/CH first (in that order),
 * the remaining countries sorted alphabetically by German name.
 *
 * Computed once at module load — the data is static and the locale is fixed.
 */
export const COUNTRY_OPTIONS: DropdownOption[] = (() => {
  const toOption = (code: string): DropdownOption => ({
    value: code,
    label: getCountryName(code),
  });

  const pinned = PINNED_COUNTRY_CODES.map(toOption);

  const rest = COUNTRY_CODES.filter((code) => !PINNED_COUNTRY_CODES.includes(code))
    .map(toOption)
    .sort((a, b) => collator.compare(a.label, b.label));

  return [...pinned, ...rest];
})();
